import {
    type MutableRefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Maximize2 } from '@/components/icons';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { APP_FONT } from '@/constants';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { AIImprovementIntent } from '@/api/ai';

const VIEWPORT_MARGIN = 16;
const DESKTOP_MAX_EDITOR_HEIGHT = 480;
// Keep the panel usable (toolbar + min-h-[180px] content + footer) even when the
// clicked section's top sits near the bottom of the visible container.
const MIN_EDITOR_HEIGHT = 260;
const PREVIEW_CONTAINER_SELECTOR = '[data-preview-container]';

type FloatingSectionEditorProps = {
    anchorRef: RefObject<HTMLElement | null>;
    editorRef: MutableRefObject<HTMLDivElement | null>;
    aiPanelRef: MutableRefObject<HTMLDivElement | null>;
    value: string;
    onChange: (value: string) => void;
    onSave: () => void;
    onCancel: () => void;
    isAIPanelOpen: boolean;
    onAIPanelOpenChange: (open: boolean) => void;
    isExpanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    reportId?: string;
    sectionId?: string;
    onBeforeGenerate?: () => Promise<void>;
    initialCaret?: { textOffset: number } | null;
    initialAIIntent?: AIImprovementIntent | null;
};

type FloatingSectionPosition = {
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
    isReady: boolean;
};

export default function FloatingSectionEditor({
    anchorRef,
    editorRef,
    aiPanelRef,
    value,
    onChange,
    onSave,
    onCancel,
    isAIPanelOpen,
    onAIPanelOpenChange,
    isExpanded,
    onExpandedChange,
    reportId,
    sectionId,
    onBeforeGenerate,
    initialCaret,
    initialAIIntent,
}: FloatingSectionEditorProps) {
    const isMobile = useIsMobile();
    const [position, setPosition] = useState<FloatingSectionPosition>({
        top: 0,
        left: 0,
        width: 0,
        maxHeight: 0,
        placement: 'bottom',
        isReady: false,
    });
    const animationFrameRef = useRef<number | null>(null);

    const syncPosition = useCallback(() => {
        if (isMobile) {
            setPosition((prev) => ({ ...prev, isReady: true }));
            return;
        }

        const anchor = anchorRef.current;

        if (!anchor) return;

        const rect = anchor.getBoundingClientRect();
        const scrollContainer = document.querySelector(PREVIEW_CONTAINER_SELECTOR);
        const containerRect = scrollContainer?.getBoundingClientRect();

        // Hide only when the section is fully out of view. (Hiding as soon as the
        // section's *top* scrolls past the container top wrongly hid the editor when
        // clicking near the bottom of a tall, multi-page section.)
        if (
            containerRect &&
            (rect.bottom <= containerRect.top || rect.top >= containerRect.bottom)
        ) {
            setPosition((prev) => (prev.isReady ? { ...prev, isReady: false } : prev));
            return;
        }

        const minLeft = containerRect
            ? Math.max(VIEWPORT_MARGIN, containerRect.left + VIEWPORT_MARGIN)
            : VIEWPORT_MARGIN;
        const maxRight = containerRect
            ? Math.min(window.innerWidth - VIEWPORT_MARGIN, containerRect.right - VIEWPORT_MARGIN)
            : window.innerWidth - VIEWPORT_MARGIN;
        const availableWidth = maxRight - minLeft;

        if (availableWidth <= 0) {
            setPosition((prev) => (prev.isReady ? { ...prev, isReady: false } : prev));
            return;
        }

        const width = Math.min(rect.width, availableWidth);
        const left = Math.max(minLeft, Math.min(rect.left, maxRight - width));

        const minTop = containerRect
            ? Math.max(VIEWPORT_MARGIN, containerRect.top + VIEWPORT_MARGIN)
            : VIEWPORT_MARGIN;
        const maxBottom = containerRect
            ? Math.min(window.innerHeight - VIEWPORT_MARGIN, containerRect.bottom - VIEWPORT_MARGIN)
            : window.innerHeight - VIEWPORT_MARGIN;
        const viewportHeight = maxBottom - minTop;

        if (viewportHeight <= 0) {
            setPosition((prev) => (prev.isReady ? { ...prev, isReady: false } : prev));
            return;
        }

        // Anchor at the section top, but clamp into the visible container so the
        // panel is never pushed off-screen or squeezed below a usable height:
        // when the section top is scrolled above the viewport this pins to minTop
        // (full height); when it sits near the bottom, `top` is pulled up so at
        // least MIN_EDITOR_HEIGHT of room remains.
        const preferredTop = Math.max(minTop, rect.top);
        const top = Math.min(preferredTop, Math.max(minTop, maxBottom - MIN_EDITOR_HEIGHT));
        // maxBottom - top is inherently <= viewportHeight since top >= minTop.
        const maxHeight = Math.min(DESKTOP_MAX_EDITOR_HEIGHT, maxBottom - top);

        setPosition((prev) => {
            const nextPosition = {
                top,
                left,
                width,
                maxHeight,
                placement: 'bottom',
                isReady: true,
            } as FloatingSectionPosition;

            if (
                prev.top === nextPosition.top &&
                prev.left === nextPosition.left &&
                prev.width === nextPosition.width &&
                prev.maxHeight === nextPosition.maxHeight &&
                prev.placement === nextPosition.placement &&
                prev.isReady === nextPosition.isReady
            ) {
                return prev;
            }

            return nextPosition;
        });
    }, [anchorRef, isMobile]);

    const scheduleSyncPosition = useCallback(() => {
        if (animationFrameRef.current !== null) return;

        animationFrameRef.current = window.requestAnimationFrame(() => {
            animationFrameRef.current = null;
            syncPosition();
        });
    }, [syncPosition]);

    useLayoutEffect(() => {
        scheduleSyncPosition();

        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [scheduleSyncPosition]);

    // Reveal on open: clicking the continuation fragment of a block that
    // pagination had split across pages un-splits it back onto its head page,
    // which can leave the anchor (and the panel pinned to it) above the
    // visible container. Scroll the container minimally so the editor is in
    // view; for ordinary clicks the anchor is already visible and nothing
    // scrolls.
    useEffect(() => {
        if (isMobile) return undefined;

        const frame = requestAnimationFrame(() => {
            const anchor = anchorRef.current;
            const container = document.querySelector(PREVIEW_CONTAINER_SELECTOR);
            if (!anchor || !container) return;

            const anchorTop = anchor.getBoundingClientRect().top;
            const revealTop = container.getBoundingClientRect().top + VIEWPORT_MARGIN;
            if (anchorTop < revealTop - 1) {
                container.scrollBy({ top: anchorTop - revealTop, behavior: 'smooth' });
            }
        });

        return () => cancelAnimationFrame(frame);
        // Mount-only by design: reveal once when the editor opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Escape while the caret is inside the editor: ProseMirror preventDefaults
    // Escape on its own, so the document listener below never gets a usable
    // event and the panel used to stay open for the (common) case of a user
    // who had actually typed something.
    const handleEditorEscape = useCallback(() => {
        if (isAIPanelOpen || isExpanded) return;
        onCancel();
    }, [isAIPanelOpen, isExpanded, onCancel]);

    useEffect(() => {
        const handleEscClose = (event: KeyboardEvent) => {
            if (isAIPanelOpen || isExpanded || event.defaultPrevented || event.key !== 'Escape') {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            onCancel();
        };

        document.addEventListener('keydown', handleEscClose);

        return () => {
            document.removeEventListener('keydown', handleEscClose);
        };
    }, [isAIPanelOpen, isExpanded, onCancel]);

    useEffect(() => {
        if (isMobile) {
            setPosition((prev) => ({ ...prev, isReady: true }));
            return;
        }

        const scrollContainer = document.querySelector(PREVIEW_CONTAINER_SELECTOR);
        const anchor = anchorRef.current;
        const zoomElement = document.querySelector('[data-report-zoom]');
        const containerResizeObserver = scrollContainer
            ? new ResizeObserver(() => {
                  scheduleSyncPosition();
              })
            : null;
        const resizeObserver = anchor
            ? new ResizeObserver(() => {
                  scheduleSyncPosition();
              })
            : null;
        const mutationObserver = new MutationObserver(scheduleSyncPosition);

        scrollContainer?.addEventListener('scroll', scheduleSyncPosition, {
            passive: true,
        });
        window.addEventListener('resize', scheduleSyncPosition);

        if (anchor && resizeObserver) {
            resizeObserver.observe(anchor);
        }

        if (scrollContainer && containerResizeObserver) {
            containerResizeObserver.observe(scrollContainer);
        }

        if (zoomElement) {
            mutationObserver.observe(zoomElement, {
                attributes: true,
                attributeFilter: ['data-report-zoom'],
            });
        }

        return () => {
            scrollContainer?.removeEventListener('scroll', scheduleSyncPosition);
            window.removeEventListener('resize', scheduleSyncPosition);
            containerResizeObserver?.disconnect();
            resizeObserver?.disconnect();
            mutationObserver.disconnect();
        };
    }, [anchorRef, isMobile, scheduleSyncPosition]);

    if (typeof document === 'undefined') {
        return null;
    }

    const isFloatingVisible = position.isReady && !isAIPanelOpen && !isExpanded;

    return createPortal(
        <div className="pointer-events-none fixed inset-0 z-[70]">
            <div
                ref={(node) => {
                    editorRef.current = node;
                }}
                data-floating-section-editor
                data-placement={isMobile ? 'fullscreen' : position.placement}
                className={cn(
                    'force-light flex flex-col overflow-hidden bg-white transition-[opacity,transform] duration-200 ease-out',
                    isMobile
                        ? 'fixed inset-0 rounded-none border-0 shadow-none'
                        : 'fixed rounded-2xl border-2 border-blue-500 shadow-2xl',
                    isFloatingVisible
                        ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
                        : 'translate-y-1 scale-[0.985] opacity-0',
                    (isAIPanelOpen || isExpanded) && 'pointer-events-none',
                )}
                style={
                    isMobile
                        ? {
                              visibility: isFloatingVisible ? 'visible' : 'hidden',
                          }
                        : {
                              top: position.top,
                              left: position.left,
                              width: position.width,
                              maxHeight: position.maxHeight,
                              visibility: isFloatingVisible ? 'visible' : 'hidden',
                          }
                }
            >
                <RichTextEditor
                    value={value}
                    onChange={onChange}
                    className="min-h-0 flex-1 rounded-none border-0 shadow-none"
                    viewportClassName={cn(
                        'max-h-none min-h-[180px] flex-1 overflow-y-auto',
                        isMobile && 'min-h-0',
                    )}
                    reportId={reportId}
                    sectionId={sectionId}
                    onBeforeGenerate={onBeforeGenerate}
                    onAIPanelOpenChange={onAIPanelOpenChange}
                    aiPanelRef={aiPanelRef}
                    aiPanelMode="floating"
                    initialCaret={initialCaret}
                    initialAIIntent={initialAIIntent}
                    onEscape={handleEditorEscape}
                />

                <div
                    className={cn(
                        'shrink-0 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5',
                        isMobile && 'pb-[calc(env(safe-area-inset-bottom)+12px)]',
                    )}
                    style={{ fontFamily: APP_FONT }}
                >
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={onSave}
                                className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90"
                            >
                                Salvar
                            </button>
                            <button
                                type="button"
                                onClick={onCancel}
                                className="rounded bg-slate-200 px-3 py-1 text-xs text-slate-700 hover:bg-slate-300"
                            >
                                Cancelar
                            </button>
                        </div>
                        {!isMobile && (
                            <button
                                type="button"
                                onClick={() => onExpandedChange(true)}
                                className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                                title="Expandir editor"
                            >
                                <Maximize2 className="size-3.5" />
                                Expandir
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {isExpanded && (
                <Dialog open onOpenChange={(open: boolean) => !open && onExpandedChange(false)}>
                    <DialogContent
                        className="force-light flex h-[85vh] w-[90vw] max-w-3xl flex-col gap-0 overflow-hidden p-0"
                        onInteractOutside={(event: Event) => {
                            if (aiPanelRef.current?.contains(event.target as Node)) {
                                event.preventDefault();
                            }
                        }}
                        style={{ fontFamily: APP_FONT }}
                    >
                        <DialogHeader className="border-b border-slate-200 px-5 py-3 text-left">
                            <DialogTitle>Editar seção</DialogTitle>
                        </DialogHeader>

                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                            {/* No initialAIIntent here on purpose: this editor
                                mounts when the user expands, which happens after
                                the context-menu preset has already run in the
                                floating one. Passing it would fire a second
                                generation over the first one's result. */}
                            <RichTextEditor
                                value={value}
                                onChange={onChange}
                                className="min-h-0 flex-1 rounded-none border-0 shadow-none"
                                viewportClassName="min-h-0 flex-1 overflow-y-auto"
                                fillHeight
                                reportId={reportId}
                                sectionId={sectionId}
                                onBeforeGenerate={onBeforeGenerate}
                                onAIPanelOpenChange={onAIPanelOpenChange}
                                aiPanelRef={aiPanelRef}
                                aiPanelMode="floating"
                            />
                        </div>

                        <DialogFooter className="border-t border-slate-200 px-5 py-3">
                            <button
                                type="button"
                                onClick={onCancel}
                                className="rounded bg-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-300"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={onSave}
                                className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary/90"
                            >
                                Salvar
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>,
        document.body,
    );
}
